import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CHART } from "@/lib/helpers";
import { Trophy, TrendingDown, Medal } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const rankBadge = (r) => (r === 1 ? "bg-gold text-navy" : r === 2 ? "bg-slate-300 text-navy" : r === 3 ? "bg-amber-700 text-white" : "bg-slate-100 text-slate-600");

export default function Performance() {
  const { data, isLoading } = useQuery({ queryKey: ["performance"], queryFn: async () => (await api.get("/performance")).data, refetchInterval: 30000 });

  if (isLoading) return (<div><PageHeader title="Performance Dashboard" subtitle="Auto-ranked employee performance" /><Skeleton className="h-64 rounded-lg" /></div>);

  const { ranking, top_performer, lowest_performer } = data;

  return (
    <div data-testid="performance-page">
      <PageHeader title="Performance Dashboard" subtitle="Auto-ranked employee performance & leaderboard" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="rounded-lg border-slate-200 bg-gold text-navy-900 p-6 flex items-center gap-4" data-testid="top-performer-card">
          <div className="rounded-lg bg-navy/15 p-3"><Trophy className="h-7 w-7 text-navy-900" /></div>
          <div>
            <p className="text-navy-900 text-sm font-medium">Top Performer</p>
            <p className="font-heading text-2xl font-extrabold tracking-tight">{top_performer?.name}</p>
            <p className="text-navy/70 text-xs">Overall {top_performer?.overall}% · Accuracy {top_performer?.accuracy}%</p>
          </div>
        </Card>
        <Card className="rounded-lg border-slate-200 bg-white p-6 flex items-center gap-4" data-testid="lowest-performer-card">
          <div className="rounded-lg bg-red-50 p-3"><TrendingDown className="h-7 w-7 text-red-500" /></div>
          <div>
            <p className="text-red-500 text-sm font-medium">Needs Attention</p>
            <p className="font-heading text-2xl font-extrabold tracking-tight text-navy">{lowest_performer?.name}</p>
            <p className="text-slate-500 text-xs">Overall {lowest_performer?.overall}% · Attendance {lowest_performer?.attendance}%</p>
          </div>
        </Card>
      </div>

      <SectionCard title="Overall Rating Comparison" className="mb-6" testId="performance-chart">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={ranking}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={-15} textAnchor="end" height={60} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#64748b" }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="completion" name="Completion %" fill={CHART.navy} radius={[4, 4, 0, 0]} />
            <Bar dataKey="accuracy" name="Accuracy %" fill={CHART.gold} radius={[4, 4, 0, 0]} />
            <Bar dataKey="attendance" name="Attendance %" fill={CHART.green} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </SectionCard>

      <SectionCard title="Leaderboard" testId="leaderboard-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {["Rank", "Employee", "Assigned", "Completed", "Completion %", "Productivity %", "Attendance %", "Avg Hours", "Accuracy %", "Overall"].map((h) => <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {ranking.map((r) => (
                <TableRow key={r.employee_id} data-testid={`rank-row-${r.employee_id}`} className="hover:bg-slate-50">
                  <TableCell><Badge className={`${rankBadge(r.rank)} rounded-full`}>{r.rank <= 3 && <Medal className="h-3 w-3 mr-1" />}{r.rank}</Badge></TableCell>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell>{r.assigned}</TableCell>
                  <TableCell>{r.completed}</TableCell>
                  <TableCell>{r.completion}%</TableCell>
                  <TableCell>{r.productivity}%</TableCell>
                  <TableCell>{r.attendance}%</TableCell>
                  <TableCell>{r.avg_hours}</TableCell>
                  <TableCell>{r.accuracy}%</TableCell>
                  <TableCell className="font-heading font-bold text-navy">{r.overall}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </SectionCard>
    </div>
  );
}
